import { useEffect, useRef, useCallback, useState } from 'react';
import store from '../redux/store/index';
import { apiUrl } from '../constants';

/**
 * useRingSSE – subscribes to the backend SSE stream for real-time ring health
 * parameter updates.  Whenever the ring pushes BP, HR, SPO2, or Temperature
 * data the `onUpdate` callback is called with the parsed event payload:
 *
 *   { type: 'blood_pressure' | 'heart_rate' | 'spo2' | 'temperature' | 'sync',
 *     data: { ...raw ring fields },
 *     timestamp: <unix ms> }
 *
 * The hook handles:
 *  - Initial connection with Bearer token passed as a query param (EventSource
 *    does not support custom headers natively in the browser).
 *  - Exponential-backoff reconnection on network errors.
 *  - Cleanup on component unmount.
 *
 * @param {function} onUpdate   - Called each time a `ring-health-update` event arrives
 * @param {boolean}  [enabled]  - Set to false to pause the subscription (default: true)
 * @returns {{ connected: boolean, lastEvent: object|null, error: string|null }}
 */
export function useRingSSE(onUpdate, enabled = true) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [error, setError] = useState(null);

  const esRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(2000); // start at 2 s, cap at 30 s
  const onUpdateRef = useRef(onUpdate);

  // Keep callback reference fresh without restarting the SSE connection
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const connect = useCallback(() => {
    // Obtain current access token from redux store
    const token = store.getState()?.auth?.token;
    if (!token) {
      setError('No auth token – cannot open SSE stream');
      return;
    }

    const url = `${apiUrl}api/v1/patient/ring-health-stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('connected', () => {
      setConnected(true);
      setError(null);
      retryDelayRef.current = 2000; // reset backoff on successful connection
    });

    es.addEventListener('ring-health-update', (e) => {
      try {
        const payload = JSON.parse(e.data);
        setLastEvent(payload);
        onUpdateRef.current?.(payload);
      } catch (err) {
        console.error('[useRingSSE] Failed to parse event data:', err);
      }
    });

    es.onerror = () => {
      setConnected(false);
      es.close();
      esRef.current = null;

      const delay = retryDelayRef.current;
      retryDelayRef.current = Math.min(delay * 2, 30000);
      setError(`SSE connection lost. Reconnecting in ${delay / 1000}s…`);

      retryTimerRef.current = setTimeout(() => {
        if (esRef.current === null) connect();
      }, delay);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return;
    connect();

    return () => {
      clearTimeout(retryTimerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setConnected(false);
    };
  }, [enabled, connect]);

  return { connected, lastEvent, error };
}
